import React, { useEffect, useState } from 'react';
import { noteSignupIntent } from '../utils/signupIntent';
import { usePageMeta } from '../utils/pageMeta';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, updateDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Box, CircularProgress, TextField, Button } from '@mui/material';
import WaiverDialog from '../components/WaiverDialog';
import { hasValidWaiverAcceptance } from '../utils/waiverGate';
import { useBranding } from '../contexts/BrandingContext';

const gold = '#C9A84C';
const goldFaint = 'rgba(201,168,76,0.15)';
const goldMid = 'rgba(201,168,76,0.6)';
const dark = '#0D1E35';

const DAY_ORDER = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

// Compress a list of day codes into a contiguous range string. e.g. ['Mon','Tue','Wed','Thu','Fri'] → "Mon–Fri"
function fmtDays(days) {
  if (!days || !days.length) return '';
  const sorted = [...days].sort((a,b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  const idxs = sorted.map(d => DAY_ORDER.indexOf(d));
  const isContiguous = idxs.every((v, i) => i === 0 || v === idxs[i-1] + 1);
  if (isContiguous && sorted.length > 2) return `${sorted[0]}–${sorted[sorted.length-1]}`;
  return sorted.join(', ');
}

const TIER_DISPLAY = {
  daypass: {
    tagline: 'One-time access',
    bullets: [
      'Single-day facility access',
      'Play with members or guests',
      'No account required',
      'No commitment',
    ],
    accent: '#7BA8C9',
    emphasize: false,
    ctaLabel: 'Buy Day Pass →',
  },
  'day pass': {
    tagline: 'One-time access',
    bullets: [
      'Single-day facility access',
      'Play with members or guests',
      'No account required',
      'No commitment',
    ],
    accent: '#7BA8C9',
    emphasize: false,
    ctaLabel: 'Buy Day Pass →',
  },
  silver: {
    tagline: 'Most popular',
    bullets: [
      'Access to {facility} courts',
      'Reserve court time each week',
      '1 guest pass per month',
      'Member-only events',
      'Cancel anytime',
    ],
    accent: '#C0C0C0',
    emphasize: true,
    ctaLabel: 'Join Now →',
  },
  gold: {
    tagline: 'Maximum access',
    bullets: [
      'Access to {facility} courts',
      'More weekly court time',
      'Prime-time hours included',
      '1 guest pass per month',
      'Priority booking access',
    ],
    accent: gold,
    emphasize: false,
    ctaLabel: 'Join Now →',
  },
};

const DEFAULT_DISPLAY = {
  tagline: 'Membership',
  bullets: ['Access to {facility} courts', 'Member benefits'],
  accent: gold,
  emphasize: false,
  ctaLabel: 'Join Now →',
};

export default function AdultMembership() {
  const branding = useBranding();
  usePageMeta('Memberships | {club}', 'Flexible beach volleyball membership plans and day passes at {facility} in Orlando, FL.');
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [primeBlocks, setPrimeBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [subscribing, setSubscribing] = useState(null);
  // Waiver gate
  const [requiredWaiverId, setRequiredWaiverId] = useState('');
  // Editable "prime hours" text block (stored on settings/membershipTiers)
  const [primeTitle, setPrimeTitle] = useState('What are prime hours?');
  const [primeText, setPrimeText] = useState('Prime hours are the peak-demand times listed below. Bookings overlapping these windows draw from your prime hour allocation; all other times draw from your regular hours.');
  const [editingPrime, setEditingPrime] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftText, setDraftText] = useState('');
  const [savingPrime, setSavingPrime] = useState(false);
  const [waiverDoc, setWaiverDoc] = useState(null);          // the loaded document
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);      // tier to subscribe to after acceptance

  // Someone browsing Memberships before signing up gets the adult survey with
  // this option already ticked (utils/signupIntent).
  useEffect(() => { noteSignupIntent('member'); }, []);

  useEffect(() => {
    document.title = 'Adult Membership · Torch Beach Volleyball';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@300;400;600;700&display=swap';
    document.head.appendChild(link);

    const unsub = onAuthStateChanged(auth, async (user) => {
      let admin = false;
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const roles = userDoc.data().roles || [];
            admin = roles.includes('admin');
          }
        } catch (e) {
          console.error('Admin check failed:', e);
        }
      }
      setIsAdmin(admin);
      setAuthReady(true);

      // Load tiers for everyone — the AdultMembership page is now public.
      try {
        const [tiersSnap, blocksSnap, settingsSnap] = await Promise.all([
          getDocs(collection(db, 'membershipTiers')),
          getDocs(collection(db, 'rentalTimeBlocks')),
          getDoc(doc(db, 'settings', 'membershipTiers')),
        ]);
        const all = tiersSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          // court_rental tiers have no price and no Stripe product — they are
          // assigned by an admin, so they never belong on the public page.
          .filter(t => t.enabled !== false && t.pricingMode !== 'court_rental' && (!t.adminOnly || admin))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setTiers(all);

        if (settingsSnap.exists()) {
          if (settingsSnap.data().primeHoursTitle) setPrimeTitle(settingsSnap.data().primeHoursTitle);
          if (settingsSnap.data().primeHoursText) setPrimeText(settingsSnap.data().primeHoursText);
        }
        const allBlocks = blocksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPrimeBlocks(allBlocks.filter(b => b.isPrime === true));

        // Capture the gating waiver doc id (if any) and pre-load its content
        // so the dialog opens instantly when needed.
        const wid = settingsSnap.exists() ? (settingsSnap.data().requiredWaiverId || '') : '';
        setRequiredWaiverId(wid);
        if (wid) {
          try {
            const wd = await getDoc(doc(db, 'clubDocuments', wid));
            if (wd.exists()) setWaiverDoc({ id: wd.id, ...wd.data() });
          } catch (e) {
            console.warn('Waiver doc load failed:', e);
          }
        }
      } catch (e) {
        console.error('AdultMembership load:', e);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  function displayFor(tier) {
    // Fallback to hardcoded defaults keyed by tier name (legacy support
    // for tiers that haven't been edited since text-editing was added).
    const key = (tier.name || '').toLowerCase();
    const fallback = TIER_DISPLAY[key] || DEFAULT_DISPLAY;

    // Per-tier overrides from Firestore take precedence over the fallback.
    // Empty/missing values fall through to the fallback so existing tiers keep working.
    const bulletsFromTier = Array.isArray(tier.bullets) ? tier.bullets : [];
    return {
      tagline:       (tier.tagline       && tier.tagline.trim())       || fallback.tagline,
      bullets:       bulletsFromTier.length > 0 ? bulletsFromTier      : fallback.bullets,
      ctaLabel:      (tier.ctaLabel      && tier.ctaLabel.trim())      || fallback.ctaLabel,
      priceSuffix:   (tier.priceSuffix   && tier.priceSuffix.trim())   || null, // null = auto
      priceFootnote: (tier.priceFootnote && tier.priceFootnote.trim()) || null, // null = auto
      accent:        (tier.accentColor   && tier.accentColor.trim())   || fallback.accent,
      // emphasize: explicit boolean on tier wins; otherwise fall back
      emphasize: typeof tier.emphasize === 'boolean' ? tier.emphasize : !!fallback.emphasize,
    };
  }

  async function handleSubscribe(tier) {
    // Day passes use the mini-registration flow at /day-pass-checkout.
    // No login required, no waiver gate here — the day pass page handles it inline.
    if (tier.isDayPass) {
      navigate(`/day-pass-checkout?tierId=${tier.id}`);
      return;
    }
    if (!auth.currentUser) {
      sessionStorage.setItem('postLoginReturnTo', '/adult-membership');
      navigate('/login');
      return;
    }
    // Waiver gate: if a required waiver is configured and the user hasn't
    // accepted it (or their acceptance has expired), pop the waiver dialog
    // first. We complete the Stripe handoff inside the dialog's accept flow.
    if (requiredWaiverId && waiverDoc) {
      const valid = await hasValidWaiverAcceptance(auth.currentUser.uid, waiverDoc);
      if (!valid) {
        setPendingTier(tier);
        setWaiverDialogOpen(true);
        return;
      }
    }
    await startStripeCheckout(tier);
  }

  // Called by WaiverDialog when the user accepts. Writes the acceptance and
  // resumes the Stripe checkout for the originally-clicked tier. Throws to
  // surface errors back to the dialog so it can show them.
  async function handleAcceptWaiver(entry) {
    const user = auth.currentUser;
    if (!user || !waiverDoc || !pendingTier) {
      throw new Error('Session expired. Please refresh and try again.');
    }
    await updateDoc(doc(db, 'users', user.uid), {
      [`documentAcceptances.${waiverDoc.id}`]: entry,
    });
    // Capture tier before clearing state — startStripeCheckout will navigate
    // away from this page anyway, but the close-then-checkout sequence is
    // cleaner this way.
    const tier = pendingTier;
    setWaiverDialogOpen(false);
    setPendingTier(null);
    await startStripeCheckout(tier);
  }

  // Extracted so it can be called both after acceptance and when the gate
  // passes immediately.
  async function startStripeCheckout(tier) {
    setSubscribing(tier.id);
    try {
      const functions = getFunctions();
      const fnName = tier.pricingMode === 'one_time'
        ? 'createMembershipOneTimeSession'
        : 'createMembershipSubscriptionSession';
      const createSession = httpsCallable(functions, fnName);
      const result = await createSession({
        tierId: tier.id,
        successUrl: `${window.location.origin}/adult-membership?subscribed=1`,
        cancelUrl: `${window.location.origin}/adult-membership`,
      });
      window.location.href = result.data.url;
    } catch (err) {
      console.error('Subscribe failed:', err);
      alert('Subscription failed: ' + (err.message || 'Unknown error'));
      setSubscribing(null);
    }
  }

  const showComingSoon = authReady && !loading && tiers.length === 0;

  return (
    <Box sx={{
      minHeight: '100vh', background: dark, display: 'flex',
      flexDirection: 'column', alignItems: 'center',
      padding: '36px 16px 56px', fontFamily: '"Barlow", sans-serif', color: 'white',
    }}>
      <Box
        onClick={() => navigate('/?dashboard=1')}
        sx={{
          alignSelf: 'flex-start', color: goldMid, fontSize: '0.8rem', fontWeight: 600,
          letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', mb: 3,
          '&:hover': { color: gold },
        }}
      >
        ← Back to Dashboard
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, textAlign: 'center' }}>
        <img src="/horzlogo.png" alt="Torch Beach Volleyball"
             style={{ maxWidth: 280, width: '70vw', objectFit: 'contain', marginBottom: 16 }} />
        <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(2rem,5vw,3rem)', letterSpacing: '4px', lineHeight: 1 }}>
          Adult Membership
        </Box>
        <Box sx={{ fontSize: '0.85rem', fontWeight: 300, color: 'rgba(255,255,255,0.45)', mt: 1, letterSpacing: '0.5px', maxWidth: 540 }}>
          Reserve court time, bring a guest, and play the way you want. Choose the option that fits your schedule.
        </Box>
        <Box sx={{ width: 60, height: 2, background: `linear-gradient(90deg, transparent, ${gold}, transparent)`, mt: 2 }} />
      </Box>

      {/* Prime hours info — shown to everyone, only if blocks configured */}
      {!loading && primeBlocks.length > 0 && (
        <Box sx={{
          mb: 4, maxWidth: 720, width: '100%',
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${goldFaint}`,
          borderRadius: '12px',
          p: 2.5,
          textAlign: 'center',
        }}>
          {editingPrime ? (
            <Box sx={{ textAlign: 'left' }}>
              <TextField fullWidth size="small" label="Title" value={draftTitle}
                onChange={e => setDraftTitle(e.target.value)}
                sx={{ mb: 1.5, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: goldFaint } }} />
              <TextField fullWidth size="small" label="Text" value={draftText} multiline rows={4}
                onChange={e => setDraftText(e.target.value)}
                sx={{ mb: 1.5, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: goldFaint } }} />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button size="small" variant="contained" disabled={savingPrime}
                  onClick={async () => {
                    setSavingPrime(true);
                    try {
                      await setDoc(doc(db, 'settings', 'membershipTiers'),
                        { primeHoursTitle: draftTitle, primeHoursText: draftText }, { merge: true });
                      setPrimeTitle(draftTitle);
                      setPrimeText(draftText);
                      setEditingPrime(false);
                    } catch (e) { alert('Save failed: ' + e.message); }
                    setSavingPrime(false);
                  }}
                  sx={{ backgroundColor: gold, color: '#0D1E35', fontWeight: 700 }}>
                  {savingPrime ? 'Saving…' : 'Save'}
                </Button>
                <Button size="small" variant="outlined" onClick={() => setEditingPrime(false)}
                  sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.6)' }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{
                fontFamily: '"Bebas Neue", sans-serif', fontSize: '0.95rem',
                letterSpacing: '2.5px', color: gold, mb: 1,
              }}>
                {primeTitle}
              </Box>
              <Box sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                {primeText}
              </Box>
              {isAdmin && (
                <Button size="small" variant="text"
                  onClick={() => { setDraftTitle(primeTitle); setDraftText(primeText); setEditingPrime(true); }}
                  sx={{ mt: 1, color: goldMid, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  ✎ Edit this section
                </Button>
              )}
            </>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 1.5 }}>
            {primeBlocks.map(b => (
              <Box key={b.id} sx={{
                fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)',
                background: 'rgba(201,168,76,0.12)',
                border: `1px solid ${goldFaint}`,
                borderRadius: '6px',
                px: 1.5, py: 0.6,
              }}>
                <strong style={{ color: gold }}>{b.name}</strong>
                {b.days && b.days.length > 0 && <span> · {fmtDays(b.days)}</span>}
                {b.startTime && b.endTime && <span> · {fmtTime(b.startTime)}–{fmtTime(b.endTime)}</span>}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {(!authReady || loading) && (
        <Box sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.9rem', py: 8 }}>Loading…</Box>
      )}

      {showComingSoon && (
        <Box sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.95rem', py: 6, textAlign: 'center', maxWidth: 480 }}>
          <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.6rem', letterSpacing: '3px', color: gold, mb: 1 }}>
            Memberships Coming Soon
          </Box>
          <Box>We're finalizing tiers and pricing. Check back shortly to join Torch Beach Volleyball as an adult member.</Box>
        </Box>
      )}

      {!loading && tiers.length > 0 && (
        <>
          <Box sx={{
            width: '100%', maxWidth: 1080,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(tiers.length, 3)}, 1fr)` },
            gap: 3,
            alignItems: 'stretch',
          }}>
            {tiers.map(tier => {
              const disp = displayFor(tier);
              const inFlight = subscribing === tier.id;
              const isOneTime = tier.pricingMode === 'one_time';
              const hasOverage = (tier.overageRegularRate || 0) > 0 || (tier.overagePrimeRate || 0) > 0 || (tier.overageWeekendRate || 0) > 0;

              return (
                <Box key={tier.id} sx={{
                  position: 'relative',
                  background: disp.emphasize ? 'rgba(201,168,76,0.07)' : 'rgba(255,255,255,0.04)',
                  border: disp.emphasize ? `1.5px solid ${goldMid}` : `1px solid ${goldFaint}`,
                  borderRadius: '16px',
                  p: { xs: 2.5, md: 3 },
                  display: 'flex', flexDirection: 'column',
                  transform: { md: disp.emphasize ? 'translateY(-12px)' : 'none' },
                  boxShadow: disp.emphasize ? '0 20px 50px rgba(0,0,0,0.4)' : 'none',
                  transition: 'transform 0.25s, border-color 0.25s, box-shadow 0.25s',
                  '&:hover': {
                    transform: { md: disp.emphasize ? 'translateY(-16px)' : 'translateY(-4px)' },
                    borderColor: 'rgba(201,168,76,0.5)',
                    boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
                  },
                }}>
                  {disp.emphasize && (
                    <Box sx={{
                      position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                      background: gold, color: dark, fontSize: '0.62rem', fontWeight: 700,
                      letterSpacing: '2px', textTransform: 'uppercase',
                      px: 1.8, py: 0.5, borderRadius: '4px', whiteSpace: 'nowrap',
                    }}>
                      {disp.tagline}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: disp.accent, flexShrink: 0 }} />
                    <Box sx={{
                      fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.4rem',
                      letterSpacing: '3px', color: 'white',
                    }}>
                      {tier.name}
                    </Box>
                    {tier.adminOnly && (
                      <Box sx={{
                        fontSize: '0.58rem', fontWeight: 700, letterSpacing: '1.5px',
                        textTransform: 'uppercase', color: goldMid,
                        border: `1px solid ${goldFaint}`, borderRadius: '4px',
                        px: 0.8, py: 0.2, flexShrink: 0,
                      }}>
                        Admin Only
                      </Box>
                    )}
                  </Box>
                  {!disp.emphasize && (
                    <Box sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.5px', mb: 2 }}>
                      {disp.tagline}
                    </Box>
                  )}
                  {disp.emphasize && <Box sx={{ mb: 2 }} />}

                  {/* Price */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2.6rem', color: gold, lineHeight: 1 }}>
                        ${tier.monthlyPrice}
                      </Box>
                      <Box sx={{ fontSize: '0.85rem', color: goldMid, fontWeight: 600 }}>
                        {disp.priceSuffix != null ? disp.priceSuffix : (isOneTime ? '' : '/month')}
                      </Box>
                    </Box>
                    <Box sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
                      {disp.priceFootnote != null ? disp.priceFootnote : (isOneTime ? 'One-time payment · Annual' : 'Billed monthly · Cancel anytime')}
                    </Box>
                  </Box>

                  {/* Marketing bullets — moved ABOVE hours/overage panels */}
                  <Box sx={{ mb: 1.5 }}>
                    {disp.bullets.map(b => b.replace('{facility}', branding.facilityName)).map(b => (
                      <Box key={b} sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.2,
                        fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)',
                        mb: 0.8, lineHeight: 1.35,
                      }}>
                        <Box sx={{
                          flexShrink: 0, width: 14, height: 14, borderRadius: '50%',
                          border: `1px solid ${goldMid}`, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          mt: '2px',
                        }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: gold }} />
                        </Box>
                        <Box>{b}</Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Hour allocations — recurring tiers only */}
                  {!isOneTime && (
                    <Box sx={{
                      background: 'rgba(255,255,255,0.04)', borderRadius: '8px', p: 1.5, mb: 1.5,
                      border: `1px solid ${goldFaint}`,
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', mb: 0.4 }}>
                        <span>Regular hours</span>
                        <span style={{ color: gold, fontWeight: 700 }}>{tier.weeklyRegularHours || 0} / week</span>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', mb: 0.4 }}>
                        <span>Prime hours</span>
                        <span style={{ color: gold, fontWeight: 700 }}>{tier.weeklyPrimeHours || 0} / week</span>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)' }}>
                        <span>Weekend hours</span>
                        <span style={{ color: gold, fontWeight: 700 }}>{tier.weeklyWeekendHours || 0} / week</span>
                      </Box>
                    </Box>
                  )}

                  {/* Overage rates — recurring tiers only */}
                  {!isOneTime && hasOverage && (
                    <Box sx={{
                      background: 'rgba(255,255,255,0.04)', borderRadius: '8px', p: 1.5, mb: 2,
                      border: `1px solid ${goldFaint}`,
                    }}>
                      <Box sx={{
                        fontSize: '0.65rem', letterSpacing: '1.5px', textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.45)', mb: 0.8, fontWeight: 600,
                      }}>
                        Overage Rates · After Weekly Allocation
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', mb: 0.4 }}>
                        <span>Regular</span>
                        <span style={{ color: 'white', fontWeight: 600 }}>${(tier.overageRegularRate || 0).toFixed(2)} / hr</span>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', mb: 0.4 }}>
                        <span>Prime</span>
                        <span style={{ color: 'white', fontWeight: 600 }}>${(tier.overagePrimeRate || 0).toFixed(2)} / hr</span>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)' }}>
                        <span>Weekend</span>
                        <span style={{ color: 'white', fontWeight: 600 }}>${(tier.overageWeekendRate || 0).toFixed(2)} / hr</span>
                      </Box>
                    </Box>
                  )}

                  {/* Spacer pushes CTA to bottom */}
                  <Box sx={{ flexGrow: 1 }} />

                  <Box
                    onClick={inFlight ? undefined : () => handleSubscribe(tier)}
                    sx={{
                      textAlign: 'center', py: 1.4, borderRadius: '8px',
                      fontFamily: '"Barlow", sans-serif', fontSize: '0.82rem', fontWeight: 700,
                      letterSpacing: '1.5px', textTransform: 'uppercase',
                      background: inFlight ? 'rgba(255,255,255,0.06)' : gold,
                      color: inFlight ? 'rgba(255,255,255,0.5)' : dark,
                      cursor: inFlight ? 'wait' : 'pointer',
                      transition: 'background 0.2s',
                      '&:hover': inFlight ? {} : { background: '#e0be6a' },
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    }}
                  >
                    {inFlight && <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.6)' }} />}
                    {inFlight ? 'Opening Stripe…' : disp.ctaLabel}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {!loading && tiers.length > 0 && !auth.currentUser && (
        <Box sx={{ mt: 5, fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
          Already a member?{' '}
          <Box
            component="span"
            onClick={() => navigate('/login')}
            sx={{
              color: gold, cursor: 'pointer', fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Sign in
          </Box>
        </Box>
      )}

      {/* Waiver gate dialog. Opens via handleSubscribe when the user has no
          current acceptance of the configured `requiredWaiverId` document.
          On accept: writes users/{uid}.documentAcceptances.{docId} and resumes
          the Stripe checkout flow for the originally-clicked tier. */}
      <WaiverDialog
        open={waiverDialogOpen}
        doc={waiverDoc}
        onClose={() => setWaiverDialogOpen(false)}
        onAccepted={handleAcceptWaiver}
      />

      <Box sx={{ mt: 5, fontSize: '0.7rem', color: 'rgba(255,255,255,0.18)', letterSpacing: '2px', textTransform: 'uppercase' }}>
        © {new Date().getFullYear()} Torch Beach Volleyball · Geneva, FL
      </Box>
    </Box>
  );
}
